import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { Chat, SubquestionsType } from "@/_pages/ai-assistant/model";
import type { ProgramQuestionsType } from "@/_pages/ai-assistant/model/types";

import {
  createInitialSubquestionData,
  formatQuestionAnswer,
  getOrderedProgramQuestions,
  getVisibleProgramQuestionIds,
  isAnswerValid,
  LAST_STEP_ID,
} from "../lib/individual-program";

type UseIndividualProgramFlowProps = {
  programQuestions: ProgramQuestionsType[];
  setChatData: Dispatch<SetStateAction<Chat[]>>;
  onSubmitProgram: (data: SubquestionsType[]) => Promise<void>;
};

export const useIndividualProgramFlow = ({
  programQuestions,
  setChatData,
  onSubmitProgram,
}: UseIndividualProgramFlowProps) => {
  const orderedQuestions = useMemo(
    () => getOrderedProgramQuestions(programQuestions),
    [programQuestions],
  );

  const [individualStep, setIndividualStep] = useState<null | string>(null);
  const [isSubmited, setIsSubmited] = useState<boolean>(false);
  const [subquestionData, setSubquestionData] = useState<SubquestionsType[]>(
    () => createInitialSubquestionData(orderedQuestions),
  );

  const stepIds = useMemo(
    () => [
      ...getVisibleProgramQuestionIds(orderedQuestions, subquestionData),
      LAST_STEP_ID,
    ],
    [orderedQuestions, subquestionData],
  );

  useEffect(() => {
    setSubquestionData(createInitialSubquestionData(orderedQuestions));
  }, [orderedQuestions]);

  useEffect(() => {
    if (individualStep === null || stepIds.includes(individualStep)) {
      return;
    }

    const fallbackStep = stepIds[stepIds.length - 1] ?? null;
    setIndividualStep(fallbackStep);
  }, [individualStep, stepIds]);

  const getStepIndex = useCallback(
    (step: string | null) => (step ? stepIds.indexOf(step) : -1),
    [stepIds],
  );

  const currentStepIndex = useMemo(
    () => getStepIndex(individualStep),
    [getStepIndex, individualStep],
  );

  const isLastStep = useMemo(() => {
    if (individualStep === null || stepIds.length === 0) {
      return false;
    }

    return currentStepIndex === stepIds.length - 1;
  }, [currentStepIndex, individualStep, stepIds.length]);

  const canGoBack = useMemo(() => {
    return currentStepIndex > 0;
  }, [currentStepIndex]);

  const setSubquestionAnswer = useCallback(
    (step: string, answer: string | string[]) => {
      setSubquestionData((prev) =>
        prev.map((item) =>
          item.stepId === step
            ? {
                ...item,
                answer,
              }
            : item,
        ),
      );
    },
    [],
  );

  const handleClickOnIndividual = useCallback(() => {
    setIsSubmited(false);
    const firstStep = stepIds[0];
    if (!firstStep) {
      return;
    }

    setSubquestionData(createInitialSubquestionData(orderedQuestions));
    setIndividualStep(firstStep);

    setChatData((prev) => [
      ...prev,
      { text: "Составить индивидуальную программу", type: "question" } as Chat,
      {
        type: "subquestion",
        subquestionStep: firstStep,
        text: "",
        question: "",
        timestamp: "",
        key: String(firstStep),
        documentId: "",
        dialogue_id: "0",
      } as Chat,
    ]);
  }, [orderedQuestions, setChatData, stepIds]);

  const handleBackToChat = useCallback(() => {
    setIndividualStep(null);
  }, []);

  const handleBackToPrevStep = useCallback(() => {
    setIndividualStep((prevStep) => {
      if (!prevStep) {
        return prevStep ?? null;
      }

      const currentIndex = stepIds.indexOf(prevStep);

      if (currentIndex <= 0) {
        return prevStep ?? null;
      }

      const currentAnswer = subquestionData.find(
        (item) => item.stepId === prevStep,
      );
      const previousStep = stepIds[currentIndex - 1];

      setSubquestionAnswer(
        prevStep,
        Array.isArray(currentAnswer?.answer) ? [] : "",
      );

      setChatData((prevChat) => {
        const nextChat = [...prevChat];

        const lastItem = nextChat[nextChat.length - 1];
        if (
          lastItem?.type === "subquestion" &&
          lastItem.subquestionStep === prevStep
        ) {
          nextChat.pop();
        }

        const prevLastItem = nextChat[nextChat.length - 1];
        if (prevLastItem?.type === "question") {
          nextChat.pop();
        }

        return nextChat;
      });

      return previousStep ?? prevStep;
    });
  }, [setChatData, setSubquestionAnswer, stepIds, subquestionData]);

  const handleNextQuestion = useCallback(() => {
    if (!individualStep) {
      return;
    }

    const currentAnswer = subquestionData.find(
      (item) => item.stepId === individualStep,
    );

    if (!currentAnswer || !isAnswerValid(currentAnswer.answer)) {
      return;
    }

    setIndividualStep((prevStep) => {
      if (!prevStep) {
        return prevStep;
      }

      const stepIndex = stepIds.indexOf(prevStep);
      const nextStep = stepIds[stepIndex + 1];
      const questionText = formatQuestionAnswer(currentAnswer.answer);

      setChatData((prev) => [
        ...prev,
        {
          text: questionText,
          type: "question",
        } as Chat,
        ...(nextStep
          ? [
              {
                type: "subquestion",
                subquestionStep: nextStep,
                text: "",
                question: "",
                timestamp: "",
                key: String(nextStep),
                documentId: "",
                dialogue_id: "",
              } as Chat,
            ]
          : []),
      ]);

      return nextStep ?? prevStep;
    });
  }, [individualStep, setChatData, stepIds, subquestionData]);

  const isNextDisabled = useMemo(() => {
    if (!individualStep) {
      return true;
    }

    const current = subquestionData.find(
      (item) => item.stepId === individualStep,
    );

    if (!current) {
      return true;
    }

    return !isAnswerValid(current.answer);
  }, [individualStep, subquestionData]);

  const handleGetProgram = useCallback(() => {
    onSubmitProgram(
      subquestionData.map((item) => {
        return {
          question: item.question,
          answer: item.answer,
          type: item.type,
        };
      }),
    );

    // setIndividualStep(null);
    setIsSubmited(true);
  }, [onSubmitProgram, subquestionData]);

  return {
    individualStep,
    canGoBack,
    isLastStep,
    isSubmited,
    isNextDisabled,
    subquestionData,
    setSubquestionAnswer,
    handleBackToChat,
    handleBackToPrevStep,
    handleClickOnIndividual,
    handleGetProgram,
    handleNextQuestion,
  };
};
